"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { X, ShieldCheck, ArrowRight } from "lucide-react";
const comparisons = [{
  category: "Business Readiness",
  generic: {
    title: "Fine for basic experiments",
    description: "Generic chat-with-documents tools can work for lightweight testing, but they often become limiting when teams need reliable deployment, stronger controls, or client-facing use."
  },
  elorag: {
    title: "Built for real business deployment",
    description: "EloRAG gives businesses and agencies a ready-to-deploy RAG platform with grounded answers, controlled knowledge access, and a structure that is usable beyond simple internal experiments."
  }
}, {
  category: "Answer Quality",
  generic: {
    title: "Weak grounding and unclear traceability",
    description: "Generic tools may return broad answers, unsupported statements, or outputs without clear evidence boundaries, which makes them harder to trust in business workflows."
  },
  elorag: {
    title: "Citations and refusal when evidence is weak",
    description: "EloRAG answers from approved content only, includes supporting citations, and refuses when retrieval evidence is insufficient instead of generating unsupported output."
  }
}, {
  category: "Control Layer",
  generic: {
    title: "Limited approval and access control",
    description: "Many tools do not give teams a structured way to control exactly what content can and cannot be used by the AI, especially across evolving document sets."
  },
  elorag: {
    title: "Approved-only retrieval with governance built in",
    description: "EloRAG adds a Draft → Review → Approved workflow so the retrieval layer stays restricted to content your team has explicitly approved for AI use."
  }
}, {
  category: "Commercial Model",
  generic: {
    title: "Harder to scale across clients or tenants",
    description: "Generic tools are often not designed for white-label delivery, tenant separation, or clearer separation between platform pricing and variable model usage."
  },
  elorag: {
    title: "White-label ready with BYO-Key pricing",
    description: "EloRAG supports multi-tenant delivery, agency use cases, and BYO-Key cost control, making it easier to deploy RAG for your own business or for clients under your brand."
  }
}];
function ComparisonRow({
  item,
  index
}: {
  item: typeof comparisons[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <div ref={ref} data-index={index} className={`relative flex flex-col gap-4 transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
      <h3 className="text-center text-xl font-semibold text-foreground lg:hidden lg:text-left">
        {item.category}
      </h3>

      <div className="relative grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
        <div className="absolute left-1/2 top-1/2 z-10 hidden size-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background text-sm font-bold text-muted-foreground shadow-sm lg:flex">
          VS
        </div>

        <Card className="relative overflow-hidden border border-border/70 bg-background shadow-[0_10px_28px_-18px_rgba(0,0,0,0.12)]">
          <div className="absolute left-0 top-0 h-full w-1 bg-destructive/50" />
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <X className="size-4" />
              </div>
              <CardTitle className="text-base font-medium text-muted-foreground">
                Generic AI Tools
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {item.category}
            </div>
            <h4 className="mb-2 text-lg font-semibold text-foreground">{item.generic.title}</h4>
            <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">
              {item.generic.description}
            </p>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border border-primary/25 bg-primary/[0.025] shadow-[0_12px_32px_-18px_rgba(0,0,0,0.14)] ring-1 ring-primary/10">
          <div className="absolute left-0 top-0 h-full w-1 bg-primary" />
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary">
                <ShieldCheck className="size-4" />
              </div>
              <CardTitle className="text-base font-semibold text-primary">EloRAG</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-primary/80">
              {item.category}
            </div>
            <h4 className="mb-2 text-lg font-semibold text-foreground">{item.elorag.title}</h4>
            <p className="text-sm leading-relaxed text-foreground/80 sm:text-base">
              {item.elorag.description}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function WhyEloragVsGenericAiTools() {
  return <section id="why-elorag-vs-generic-ai-tools" className="overflow-hidden bg-background py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto mb-12 max-w-3xl text-center lg:mb-14">
          <h2 className="text-base font-semibold uppercase tracking-wider text-primary">
            Why EloRAG
          </h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Why businesses outgrow generic chat-with-documents tools
          </p>
          <p className="mt-5 text-lg leading-8 text-muted-foreground">
            Generic tools can be enough for simple use cases. EloRAG is built for businesses and agencies
            that need a real RAG deployment with grounded answers, approved-only retrieval, clearer cost
            control, and stronger operational structure.
          </p>
        </div>

        <div className="flex flex-col gap-10 lg:gap-12">
          {comparisons.map((item, i) => <ComparisonRow key={i} item={item} index={i} />)}
        </div>

        <div className="mt-14 flex flex-col items-center justify-center gap-4 sm:flex-row lg:mt-16">
          <Button size="lg" className="w-full sm:w-auto" asChild>
            <Link to="/BookADemoContactSalesPage">
              Book a Demo
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
          <Button size="lg" variant="outline" className="w-full sm:w-auto" asChild>
            <Link to="/SecurityAndGovernancePage">Explore Governance</Link>
          </Button>
        </div>
      </div>
    </section>;
}